import { BarChart3, CheckCircle2, Handshake, MessageSquare, ShieldCheck, User, Users } from 'lucide-react';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { asset, tenant } from '../../config/tenant';
import SiteFooter from '../public-home/components/SiteFooter';
import SiteHeader from '../public-home/components/SiteHeader';
import IndicacaoForm from './components/IndicacaoForm';
import { IndicacaoRoot } from './IndicacaoPage.styles';

const HEADER_OFFSET = 80;

/** Fotografia do hero (assets/indicacao-hero.jpg do pacote). */
const HERO_PHOTO = asset('/images/indicacao/hero.jpg');

const STEPS = [
	{
		num: '01',
		Icon: User,
		title: 'Cadastre a indicação',
		text: 'Informe os dados do lojista que você deseja indicar.',
	},
	{
		num: '02',
		Icon: MessageSquare,
		title: 'Nossa equipe entra em contato',
		text: 'Faremos o contato para apresentar a plataforma.',
	},
	{
		num: '03',
		Icon: Handshake,
		title: 'O lojista conhece as soluções',
		text: 'Apresentamos os benefícios e tiramos as dúvidas.',
	},
	{
		num: '04',
		Icon: CheckCircle2,
		title: 'Mais negócios para todos',
		text: 'Conectamos lojistas a novas oportunidades no mercado.',
	},
];

/**
 * Página pública de cadastro de lojista (/cadastro e /indicacao para visitantes).
 * O card usa a mesma regra do cadastro de revenda, com ou sem deeplink ?ref=.
 * O painel interno de indicações/comissões fica em Indicacao.tsx
 * (rota /indicacao para usuários logados).
 */
const IndicacaoPage: React.FC = () => {
	const scrollToForm = useCallback((e?: React.MouseEvent<HTMLAnchorElement>) => {
		e?.preventDefault();
		const el = document.getElementById('formulario');
		if (!el) return;
		const y = el.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
		window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
		el.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true });
	}, []);

	return (
		<IndicacaoRoot>
			<SiteHeader
				secondary={
					<Link className='cr-cad' to='/#login'>
						Acessar Plataforma
					</Link>
				}
				cta={
					<a className='cr-btn cr-btn--sm' href='#formulario' onClick={scrollToForm}>
						Cadastrar Indicação
					</a>
				}
			/>

			<main>
				{/* ===================== HERO + FORMULÁRIO ===================== */}
				<section id='inicio' className='cr-ind-hero'>
					<div className='cr-container cr-ind-hero__grid'>
						<div className='cr-ind-copy'>
							<span className='cr-eyebrow'>Indique lojistas</span>
							<h1 className='cr-hero__title'>
								Conecte lojistas
								<br />a novas <span className='cr-or'>oportunidades.</span>
							</h1>
							<p className='cr-hero__text'>
								Indique lojistas para o {tenant.appName} e ajude a fortalecer o ecossistema
								automotivo, conectando mais negócios, com segurança e transparência.
							</p>

							<div className='cr-benefits'>
								<div>
									<BarChart3 aria-hidden='true' strokeWidth={1.8} />
									<b>
										Mais oportunidades
										<br />
										de negócios
									</b>
								</div>
								<div>
									<Users aria-hidden='true' strokeWidth={1.8} />
									<b>
										Expansão da rede
										<br />
										de lojistas
									</b>
								</div>
								<div>
									<ShieldCheck aria-hidden='true' strokeWidth={1.8} />
									<b>
										Segurança e<br />
										transparência
									</b>
								</div>
							</div>
						</div>

						<div className='cr-ind-photo'>
							<img
								src={HERO_PHOTO}
								alt='Dois profissionais conversando em uma concessionária'
								width={411}
								height={564}
							/>
						</div>

						<aside className='cr-ind-form' id='formulario' aria-labelledby='tituloIndicacao'>
							<h2 className='cr-ind-form__title' id='tituloIndicacao'>
								Cadastre-se como lojista
							</h2>
							<p className='cr-ind-form__sub'>
								Preencha os campos abaixo para criar sua conta na plataforma.
							</p>
							<IndicacaoForm />
						</aside>
					</div>
				</section>

				{/* ===================== COMO FUNCIONA ===================== */}
				<section id='como-funciona-indicacao' className='cr-steps'>
					<div className='cr-container cr-steps__in'>
						<div>
							<span className='cr-eyebrow'>Como funciona</span>
							<h2 className='cr-steps__title'>
								É <em>simples</em> e <em>rápido.</em>
							</h2>
						</div>
						<ol className='cr-steps__items'>
							{STEPS.map(({ num, Icon, title, text }) => (
								<li className='cr-step' key={num}>
									<div className='cr-step__top'>
										<span className='cr-step__num' aria-hidden='true'>
											{num}
										</span>
										<Icon aria-hidden='true' strokeWidth={1.8} />
									</div>
									<b>{title}</b>
									<span>{text}</span>
								</li>
							))}
						</ol>
					</div>
				</section>
			</main>

			<SiteFooter registerLabel='Cadastrar-se' />
		</IndicacaoRoot>
	);
};

export default IndicacaoPage;
